import React, { createContext, useContext, useState, useEffect } from 'react';
import { usePageVisible } from './hooks/usePageVisible';
import { useTradingMode } from './contexts/TradingModeContext';

interface StrategyOption {
  id: number;
  name: string;
}

interface StrategyFilterContextType {
  selectedStrategyId: number | null; // null = "All Strategies"
  setSelectedStrategyId: (id: number | null) => void;
  strategies: StrategyOption[];
}

const StrategyFilterContext = createContext<StrategyFilterContextType>({
  selectedStrategyId: null,
  setSelectedStrategyId: () => {},
  strategies: [],
});

export const useStrategyFilter = () => useContext(StrategyFilterContext);

export const StrategyFilterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const pageVisible = usePageVisible();
  const { modeParam } = useTradingMode();
  const [selectedStrategyId, setSelectedStrategyId] = useState<number | null>(null);
  const [strategies, setStrategies] = useState<StrategyOption[]>([]);

  const fetchStrategies = async () => {
    try {
      const res = await fetch(`/api/v1/strategies/?mode=${modeParam}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStrategies(data.map((s: any) => ({ id: s.id, name: s.name })));
      }
    } catch {}
  };

  useEffect(() => {
    fetchStrategies();
    const interval = setInterval(() => {
      if (!document.hidden) fetchStrategies();
    }, 120000);
    return () => clearInterval(interval);
  }, [modeParam]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh immediately when tab becomes visible again
  useEffect(() => {
    if (pageVisible && strategies.length > 0) fetchStrategies();
  }, [pageVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <StrategyFilterContext.Provider value={{ selectedStrategyId, setSelectedStrategyId, strategies }}>
      {children}
    </StrategyFilterContext.Provider>
  );
};
